import { useCallback, useEffect, useState } from 'react';
import {
  useProjects,
  ProjectDetail,
  ProjectSummary,
} from '../hooks/useProjects';
import { useYesterdayProjects } from '../hooks/useYesterdayProjects';
import { apiGetRaw, apiJson } from '../api/client';
import { withDriveToken } from '../drive/drive-auth';

type TabId =
  | 'transcript'
  | 'images'
  | 'music'
  | 'speech'
  | 'captions'
  | 'videos'
  | 'documents';

const API_BASE = import.meta.env.VITE_API_BASE as string;

// `signR2Url` on the worker returns a relative /api/resource path; the
// browser would otherwise resolve that against the Firebase Hosting origin
// (which is not the Worker), producing broken asset links. Prepend the
// worker origin explicitly.
function absolute(url: string | null | undefined): string {
  if (!url) return '';
  return url.startsWith('http') ? url : `${API_BASE}${url}`;
}

// Filename slug — lowercase alphanumerics, non-alphanums collapsed to '-'.
function slugFilename(parts: Array<string | null | undefined>, ext: string): string {
  const joined = parts
    .filter((p): p is string => typeof p === 'string' && p.length > 0)
    .join('-')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const base = joined || 'file';
  return `${base}.${ext}`;
}

function extFromMime(mime: string | null | undefined): string {
  if (!mime) return 'bin';
  const m = mime.toLowerCase();
  if (m.includes('png')) return 'png';
  if (m.includes('jpeg') || m.includes('jpg')) return 'jpg';
  if (m.includes('webp')) return 'webp';
  if (m.includes('gif')) return 'gif';
  if (m.includes('wav')) return 'wav';
  if (m.includes('mpeg') || m.includes('mp3')) return 'mp3';
  return 'bin';
}

// Cross-origin-safe downloader: fetch the signed URL into a Blob, construct
// an object URL, trigger an <a download>, then revoke.
async function downloadSignedUrl(
  signedUrl: string,
  filename: string,
): Promise<void> {
  const res = await fetch(signedUrl);
  if (!res.ok) {
    throw new Error(`Download failed: HTTP ${res.status}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

function relativeTime(unixSeconds: number): string {
  const now = Date.now() / 1000;
  const delta = now - unixSeconds;
  if (delta < 60) return 'just now';
  if (delta < 3600) return `${Math.floor(delta / 60)}m ago`;
  if (delta < 86400) return `${Math.floor(delta / 3600)}h ago`;
  if (delta < 86400 * 30) return `${Math.floor(delta / 86400)}d ago`;
  return new Date(unixSeconds * 1000).toISOString().slice(0, 10);
}

function ArtifactChips({
  counts,
}: {
  counts: ProjectSummary['artifactCounts'];
}): JSX.Element | null {
  const chips: Array<[string, number]> = [
    ['img', counts.images],
    ['music', counts.music],
    ['speech', counts.speech],
    ['cap', counts.captions],
    ['video', counts.videos ?? 0],
    // Stage 38: Stage 28 markdown documents.
    ['docs', counts.documents ?? 0],
  ];
  const present = chips.filter(([, n]) => n > 0);
  if (present.length === 0) return null;
  return (
    <div className="project-chips">
      {present.map(([label, n]) => (
        <span key={label} className={`project-chip project-chip-${label}`}>
          {label} {n}
        </span>
      ))}
    </div>
  );
}

function TrashIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 7h16M9 7V4h6v3M7 7l1 12a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2l1-12M10 11v6M14 11v6"
      />
    </svg>
  );
}

function ProjectList({
  items,
  selectedId,
  onSelect,
  onDelete,
  busyId,
}: {
  items: ProjectSummary[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onDelete: (p: ProjectSummary) => void;
  busyId: string | null;
}): JSX.Element {
  return (
    <div className="projects-list">
      {items.map((p) => (
        <div
          key={p.conversationId}
          role="button"
          tabIndex={0}
          className={
            'project-row' +
            (selectedId === p.conversationId ? ' project-row-selected' : '')
          }
          onClick={() => onSelect(p.conversationId)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSelect(p.conversationId);
            }
          }}
        >
          <div className="project-row-top">
            <span className="project-koji">{p.koji}</span>
            <span className="project-time muted small">
              {relativeTime(p.lastTurnAt)}
            </span>
          </div>
          <div className="project-title">
            {p.title || '(untitled)'}
          </div>
          <ArtifactChips counts={p.artifactCounts} />
          <button
            type="button"
            className="project-row-delete"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(p);
            }}
            disabled={busyId === p.conversationId}
            aria-label={`Delete conversation ${p.title || p.koji}`}
            title="Delete"
          >
            <TrashIcon />
          </button>
        </div>
      ))}
    </div>
  );
}

function TranscriptTab({
  detail,
}: {
  detail: ProjectDetail;
}): JSX.Element {
  return (
    <div className="project-transcript">
      {detail.turns.length === 0 ? (
        <div className="muted">No turns recorded.</div>
      ) : (
        detail.turns.map((t) => (
          <div key={t.id} className={`turn turn-${t.role}`}>
            <div className="turn-role">{t.role}</div>
            <div className="turn-body">
              {t.role === 'tool' ? (
                <pre className="code-block">
                  <code>{t.tool_result ?? ''}</code>
                </pre>
              ) : (
                t.content.split(/\n{2,}/).map((p, i) => <p key={i}>{p}</p>)
              )}
            </div>
            <div className="turn-koji">{t.koji_codename}</div>
          </div>
        ))
      )}
    </div>
  );
}

function ImagesTab({ detail }: { detail: ProjectDetail }): JSX.Element {
  const { images } = detail.artifacts;
  const [busyId, setBusyId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  if (images.length === 0) return <div className="muted">No images.</div>;

  const handleDownload = async (img: typeof images[number]): Promise<void> => {
    setBusyId(img.id);
    setErr(null);
    try {
      const src = absolute(img.signedUrl);
      const filename = slugFilename(
        [img.title, img.prompt, img.id.slice(0, 8)],
        extFromMime(img.mime_type),
      );
      await downloadSignedUrl(src, filename);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="project-image-grid">
      {images.map((img) => {
        const src = absolute(img.signedUrl);
        return (
          <div
            key={img.id}
            className="project-image-tile"
            title={img.prompt ?? ''}
          >
            <img src={src} alt={img.title ?? img.prompt ?? 'image'} />
            <div className="project-image-tile-footer">
              {img.title ? (
                <div className="project-image-caption">{img.title}</div>
              ) : null}
              <button
                type="button"
                onClick={() => void handleDownload(img)}
                disabled={busyId !== null}
                className="project-image-download"
                aria-label={`Download ${img.title ?? 'image'}`}
              >
                {busyId === img.id ? 'Downloading…' : 'Download'}
              </button>
            </div>
          </div>
        );
      })}
      {err ? (
        <div className="status-missing small" role="alert">
          {err}
        </div>
      ) : null}
    </div>
  );
}

function AudioRow({
  src,
  title,
  subtitle,
}: {
  src: string;
  title: string;
  subtitle?: string | null;
}): JSX.Element {
  return (
    <div className="project-audio-row">
      <div className="project-audio-meta">
        <div className="project-audio-title">{title}</div>
        {subtitle ? (
          <div className="muted small">{subtitle}</div>
        ) : null}
      </div>
      <audio controls preload="none" src={src} />
    </div>
  );
}

function MusicTab({ detail }: { detail: ProjectDetail }): JSX.Element {
  const { music } = detail.artifacts;
  if (music.length === 0) return <div className="muted">No music.</div>;
  return (
    <div className="project-audio-list">
      {music.map((m) => (
        <AudioRow
          key={m.id}
          src={absolute(m.signedUrl)}
          title={m.title || '(untitled)'}
          subtitle={m.prompt}
        />
      ))}
    </div>
  );
}

function SpeechTab({ detail }: { detail: ProjectDetail }): JSX.Element {
  const { speech } = detail.artifacts;
  if (speech.length === 0) return <div className="muted">No speech clips.</div>;
  return (
    <div className="project-audio-list">
      {speech.map((s) => (
        <AudioRow
          key={s.id}
          src={absolute(s.signedUrl)}
          title={s.title || '(untitled)'}
          subtitle={s.voice ? `voice: ${s.voice}` : s.mode}
        />
      ))}
    </div>
  );
}

function CaptionsTab({ detail }: { detail: ProjectDetail }): JSX.Element {
  const { captions } = detail.artifacts;
  if (captions.length === 0) return <div className="muted">No captions.</div>;
  return (
    <div className="project-captions-list">
      {captions.map((c) => {
        const src = c.signedUrl ? absolute(c.signedUrl) : null;
        return (
          <div key={c.id} className="project-caption-row">
            {src ? (
              <div className="project-caption-thumb">
                <img src={src} alt="" />
              </div>
            ) : null}
            <div className="project-caption-body">{c.caption}</div>
          </div>
        );
      })}
    </div>
  );
}

function VideosTab({ detail }: { detail: ProjectDetail }): JSX.Element {
  const videos = detail.artifacts.videos ?? [];
  if (videos.length === 0) return <div className="muted">No videos.</div>;
  return (
    <div className="project-video-list">
      {videos.map((v) => (
        <div key={v.id} className="project-video-row">
          <video
            controls
            preload="metadata"
            src={absolute(v.signedUrl)}
            className="project-video-player"
            playsInline
          />
          <div className="project-video-meta">
            <div className="project-video-title">
              {v.title || v.koji_codename || '(untitled)'}
            </div>
            <div className="muted small" title={v.prompt}>
              {v.prompt}
            </div>
            <div className="muted small">
              {v.duration_seconds ? `${v.duration_seconds}s` : '—'}
              {v.aspect_ratio ? ` · ${v.aspect_ratio}` : ''}
              {v.resolution ? ` · ${v.resolution}` : ''}
              {v.model ? ` · ${v.model}` : ''}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Stage 38: documents tab — write_markdown_file artifacts.
function DocumentsTab({ detail }: { detail: ProjectDetail }): JSX.Element {
  const docs = detail.artifacts.documents ?? [];
  if (docs.length === 0) {
    return <div className="muted">No documents in this project.</div>;
  }
  const downloadDoc = (
    signedUrl: string,
    filenameSlug: string,
  ): void => {
    const url = absolute(signedUrl);
    const sep = url.includes('?') ? '&' : '?';
    const fn = `${filenameSlug}.md`;
    const a = document.createElement('a');
    a.href = `${url}${sep}dl=1&fn=${encodeURIComponent(fn)}`;
    a.download = fn;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };
  return (
    <div className="project-document-list">
      {docs.map((d) => (
        <div key={d.id} className="project-document-row">
          <div className="project-document-meta">
            <div className="project-document-title">{d.title}</div>
            <div className="muted small">
              {d.skill_name ? `skill: ${d.skill_name}` : 'no skill'}
              {' · '}
              {(d.size_bytes / 1024).toFixed(1)} KB
              {' · '}
              {relativeTime(d.created_at)}
            </div>
          </div>
          <button
            type="button"
            className="btn-secondary small"
            onClick={() => downloadDoc(d.signedUrl, d.filename_slug)}
            aria-label={`Download ${d.title}`}
          >
            ↓ Download
          </button>
        </div>
      ))}
    </div>
  );
}

// Stage 38: yesterday-projects content — list + detail with run
// table + Download ZIP. Mirrors the chat-projects sidebar/detail
// layout but driven by /api/yesterday-projects endpoints.
function YesterdayProjectsContent({
  listResult,
  selectedId,
  onSelect,
  detail,
  detailLoading,
  detailError,
}: {
  listResult: ReturnType<typeof useYesterdayProjects>;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  detail:
    | import('../hooks/useYesterdayProjects').YesterdayProjectDetail
    | null;
  detailLoading: boolean;
  detailError: string | null;
}): JSX.Element {
  const { list, loading, error, refresh } = listResult;

  const downloadYZip = (id: string, taskKoji: string): void => {
    const sep = '?';
    const url =
      `${API_BASE}/api/yesterday-projects/${encodeURIComponent(id)}/zip` +
      `${sep}dl=1`;
    // The endpoint sets Content-Disposition; the anchor's download
    // attribute provides a fallback name when Content-Disposition is
    // missing the filename param. Server already sets it; the
    // fallback is harmless.
    const a = document.createElement('a');
    a.href = url;
    a.download = `${taskKoji}-${id.slice(0, 8)}.zip`;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const downloadRun = (signedUrl: string, basename: string): void => {
    const url = absolute(signedUrl);
    const sep = url.includes('?') ? '&' : '?';
    const a = document.createElement('a');
    a.href = `${url}${sep}dl=1&fn=${encodeURIComponent(basename)}`;
    a.download = basename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  if (loading && list.length === 0) {
    return <div className="muted">Loading yesterday projects…</div>;
  }
  if (error) {
    return (
      <div className="loading-screen">
        <div className="status-missing">Error: {error}</div>
        <button onClick={() => void refresh()}>Retry</button>
      </div>
    );
  }
  if (list.length === 0) {
    return (
      <div className="projects-empty">
        <div className="muted">
          No Yesterday tasks yet. Create one in the Yesterday tab.
        </div>
      </div>
    );
  }

  return (
    <>
      <aside className="projects-sidebar">
        <div className="projects-sidebar-header">
          <strong>Yesterday</strong>
          <button
            type="button"
            className="small"
            onClick={() => void refresh()}
          >
            Refresh
          </button>
        </div>
        <div className="projects-list">
          {list.map((p) => (
            <div
              key={p.id}
              role="button"
              tabIndex={0}
              className={
                'project-row' +
                (p.id === selectedId ? ' project-row-selected' : '')
              }
              onClick={() => onSelect(p.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelect(p.id);
                }
              }}
            >
              <div className="project-row-top">
                <span className="project-koji">{p.koji}</span>
                <span className="project-time muted small">
                  {p.lastRun ? relativeTime(p.lastRun) : 'never run'}
                </span>
              </div>
              <div className="project-title">{p.name}</div>
              <div className="muted small">
                {p.scheduleSummary} · {p.status}
              </div>
              <div className="project-chips">
                <span className="project-chip project-chip-runs">
                  runs {p.runCount}
                </span>
                {p.boundPanel ? (
                  <span className="project-chip project-chip-panel">
                    panel {p.boundPanel.koji}
                  </span>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </aside>

      <section className="projects-detail">
        {!selectedId ? (
          <div className="projects-detail-empty muted">
            Select a Yesterday task from the list.
          </div>
        ) : detailLoading ? (
          <div className="muted">Loading task…</div>
        ) : detailError ? (
          <div className="status-missing">Error: {detailError}</div>
        ) : !detail ? (
          <div className="muted">No data.</div>
        ) : (
          <>
            <header className="project-detail-header">
              <div>
                <h2 className="project-detail-title">{detail.task.name}</h2>
                <div className="project-detail-meta muted small">
                  <span>{detail.task.koji}</span>
                  {' · '}
                  <span>{detail.task.scheduleSummary}</span>
                  {' · '}
                  <span>{detail.task.status}</span>
                  {' · '}
                  <span>
                    {detail.task.occurrencesUsed}/{detail.task.maxOccurrences}{' '}
                    runs
                  </span>
                  {detail.task.selfRenew ? ' · self-renew' : null}
                  {detail.task.enableTools ? ' · tools-enabled' : null}
                </div>
              </div>
              <div className="project-detail-actions">
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() =>
                    downloadYZip(detail.task.id, detail.task.koji)
                  }
                >
                  ⇩ Download ZIP
                </button>
              </div>
            </header>

            {detail.task.lastError ? (
              <div className="status-missing small">
                Last error: {detail.task.lastError}
              </div>
            ) : null}

            {detail.boundPanel ? (
              <div className="project-bound-panel muted small">
                Bound to bulletin panel:{' '}
                <strong>
                  {detail.boundPanel.displayLabel || detail.boundPanel.koji}
                </strong>
                {' '}
                (
                <code>{detail.boundPanel.koji}</code>
                ){' · '}
                <a
                  href="#/bulletin"
                  onClick={(e) => {
                    e.preventDefault();
                    window.location.hash = '#/bulletin';
                  }}
                >
                  View on canvas
                </a>
              </div>
            ) : null}

            <div className="project-tab-body">
              {detail.runs.length === 0 ? (
                <div className="muted">No runs yet.</div>
              ) : (
                <table className="project-runs-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Koji</th>
                      <th>Format</th>
                      <th>Size</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.runs.map((r) => {
                      // Basename pattern: <unixSeconds>_<koji>.<ext>
                      const match = r.basename.match(
                        /^(\d+)_([A-Za-z0-9-]+)\.([A-Za-z0-9]+)$/,
                      );
                      const koji = match?.[2] ?? '—';
                      return (
                        <tr key={r.key}>
                          <td>
                            {new Date(r.uploaded).toLocaleString()}
                          </td>
                          <td>
                            <code>{koji}</code>
                          </td>
                          <td>{r.ext}</td>
                          <td>{(r.size / 1024).toFixed(1)} KB</td>
                          <td>
                            <button
                              type="button"
                              className="btn-secondary small"
                              onClick={() => downloadRun(r.signedUrl, r.basename)}
                              aria-label={`Download ${r.basename}`}
                            >
                              ↓
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </section>
    </>
  );
}

// Stage 38: top-level Projects tab — chat-projects (default) vs
// yesterday-projects. Selection persists in localStorage.
type TopTabId = 'chat' | 'yesterday';
const TOP_TAB_STORAGE_KEY = 'synth-projects-tab';

function readInitialTopTab(): TopTabId {
  try {
    const v = localStorage.getItem(TOP_TAB_STORAGE_KEY);
    return v === 'yesterday' ? 'yesterday' : 'chat';
  } catch {
    return 'chat';
  }
}

export function ProjectsView({
  onOpenChat,
}: {
  onOpenChat: (conversationId: string) => void;
}): JSX.Element {
  const { list, loading, error, refresh, loadProject, deleteProject } =
    useProjects();
  // Stage 38: yesterday-projects companion hook.
  const yesterdayProjects = useYesterdayProjects();

  const [topTab, setTopTabState] = useState<TopTabId>(readInitialTopTab);
  const setTopTab = useCallback((next: TopTabId): void => {
    setTopTabState(next);
    try {
      localStorage.setItem(TOP_TAB_STORAGE_KEY, next);
    } catch {
      /* ignore quota / SSR cases */
    }
  }, []);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ProjectDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabId>('transcript');
  const [actionBusy, setActionBusy] = useState<string | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Stage 38: yesterday-project selection state.
  const [selectedYId, setSelectedYId] = useState<string | null>(null);
  const [yDetail, setYDetail] = useState<
    import('../hooks/useYesterdayProjects').YesterdayProjectDetail | null
  >(null);
  const [yDetailLoading, setYDetailLoading] = useState(false);
  const [yDetailError, setYDetailError] = useState<string | null>(null);

  // Reset side selection when switching top tabs so the detail
  // surface always matches the active list.
  useEffect(() => {
    if (topTab === 'chat') {
      setSelectedYId(null);
      setYDetail(null);
    } else {
      setSelectedId(null);
      setDetail(null);
    }
  }, [topTab]);

  // Stage 38: load yesterday detail when selectedYId changes.
  // Depend on the stable `openDetail` callback (useCallback with []
  // deps) — depending on the whole `yesterdayProjects` object would
  // re-fire the effect every render, causing infinite "loading…".
  const openYDetail = yesterdayProjects.openDetail;
  useEffect(() => {
    if (!selectedYId) {
      setYDetail(null);
      return;
    }
    setYDetailLoading(true);
    setYDetailError(null);
    openYDetail(selectedYId)
      .then((d) => setYDetail(d))
      .catch((e) =>
        setYDetailError(e instanceof Error ? e.message : String(e)),
      )
      .finally(() => setYDetailLoading(false));
  }, [selectedYId, openYDetail]);

  const onDelete = useCallback(
    async (p: ProjectSummary): Promise<void> => {
      const name = p.title || p.koji;
      const ok = window.confirm(
        `Delete conversation "${name}" and all its artifacts? This removes the transcript, generated images, music, speech clips, videos, captions, and uploads. Cannot be undone.`,
      );
      if (!ok) return;
      setDeletingId(p.conversationId);
      setActionMsg(null);
      try {
        await deleteProject(p.conversationId);
        if (selectedId === p.conversationId) {
          setSelectedId(null);
          setDetail(null);
        }
        setActionMsg(`Deleted "${name}".`);
      } catch (err) {
        setActionMsg(
          'Delete failed: ' +
            (err instanceof Error ? err.message : String(err)),
        );
      } finally {
        setDeletingId(null);
      }
    },
    [deleteProject, selectedId],
  );

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    setDetailLoading(true);
    setDetailError(null);
    setTab('transcript');
    loadProject(selectedId)
      .then((d) => setDetail(d))
      .catch((e) =>
        setDetailError(e instanceof Error ? e.message : String(e)),
      )
      .finally(() => setDetailLoading(false));
  }, [selectedId, loadProject]);

  const downloadZip = useCallback(async (): Promise<void> => {
    if (!selectedId || !detail) return;
    setActionBusy('zip');
    setActionMsg(null);
    try {
      const res = await apiGetRaw(
        `/api/projects/${encodeURIComponent(selectedId)}/zip`,
      );
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${detail.conversation.koji_codename}-${detail.conversation.id.slice(0, 8)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Defer revoke slightly so Safari has a chance to start the download.
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      setActionMsg('ZIP downloaded.');
    } catch (err) {
      setActionMsg(
        'ZIP failed: ' + (err instanceof Error ? err.message : String(err)),
      );
    } finally {
      setActionBusy(null);
    }
  }, [selectedId, detail]);

  const driveSync = useCallback(async (): Promise<void> => {
    if (!selectedId) return;
    setActionBusy('drive');
    setActionMsg(null);
    try {
      const result = await withDriveToken(async (token) =>
        apiJson<{ folderUrl: string; uploaded: Array<{ name: string }> }>(
          'POST',
          `/api/projects/${encodeURIComponent(selectedId)}/drive-sync`,
          { accessToken: token },
        ),
      );
      const count = result.uploaded.length;
      setActionMsg(
        count === 0
          ? 'Drive already up to date.'
          : `Synced ${count} file${count === 1 ? '' : 's'} to Drive.`,
      );
    } catch (err) {
      setActionMsg(
        'Drive sync failed: ' +
          (err instanceof Error ? err.message : String(err)),
      );
    } finally {
      setActionBusy(null);
    }
  }, [selectedId]);

  // Stage 38: top toggle bar always visible so the user can swap
  // between Chat and Yesterday regardless of either list's
  // loading/error/empty state. The sidebar / detail layout below
  // branches on topTab.
  const topToggle = (
    <div className="projects-top-tabs" role="tablist">
      <button
        type="button"
        role="tab"
        aria-selected={topTab === 'chat'}
        className={topTab === 'chat' ? 'active' : ''}
        onClick={() => setTopTab('chat')}
      >
        Chat ({list.length})
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={topTab === 'yesterday'}
        className={topTab === 'yesterday' ? 'active' : ''}
        onClick={() => setTopTab('yesterday')}
      >
        Yesterday ({yesterdayProjects.list.length})
      </button>
    </div>
  );

  // Yesterday-projects branch.
  if (topTab === 'yesterday') {
    return (
      <div className="projects-view projects-view-yesterday">
        {topToggle}
        <YesterdayProjectsContent
          listResult={yesterdayProjects}
          selectedId={selectedYId}
          onSelect={setSelectedYId}
          detail={yDetail}
          detailLoading={yDetailLoading}
          detailError={yDetailError}
        />
      </div>
    );
  }

  // Chat-projects branch (current behaviour).
  if (loading && list.length === 0) {
    return (
      <div className="projects-view loading-screen">
        {topToggle}
        Loading projects…
      </div>
    );
  }
  if (error) {
    return (
      <div className="projects-view loading-screen">
        {topToggle}
        <div className="status-missing">Error: {error}</div>
        <button onClick={() => void refresh()}>Retry</button>
      </div>
    );
  }
  if (list.length === 0) {
    return (
      <div className="projects-view projects-empty">
        {topToggle}
        <div className="muted">
          No projects yet. Start a conversation to create your first project.
        </div>
      </div>
    );
  }

  return (
    <div className="projects-view">
      {topToggle}
      <aside className="projects-sidebar">
        <div className="projects-sidebar-header">
          <strong>Projects</strong>
          <button
            type="button"
            className="small"
            onClick={() => void refresh()}
          >
            Refresh
          </button>
        </div>
        <ProjectList
          items={list}
          selectedId={selectedId}
          onSelect={setSelectedId}
          onDelete={(p) => void onDelete(p)}
          busyId={deletingId}
        />
      </aside>

      <section className="projects-detail">
        {!selectedId ? (
          <div className="projects-detail-empty muted">
            Select a project from the list.
          </div>
        ) : detailLoading ? (
          <div className="muted">Loading project…</div>
        ) : detailError ? (
          <div className="status-missing">Error: {detailError}</div>
        ) : !detail ? (
          <div className="muted">No data.</div>
        ) : (
          <>
            <header className="project-detail-header">
              <div>
                <h2 className="project-detail-title">
                  {detail.conversation.title || '(untitled)'}
                </h2>
                <div className="project-detail-meta muted small">
                  <span>{detail.conversation.koji_codename}</span>
                  {' · '}
                  <span>persona: {detail.conversation.persona_id ?? '(none)'}</span>
                  {' · '}
                  <span>{detail.conversation.turn_count} turns</span>
                  {' · '}
                  <span>
                    last {relativeTime(detail.conversation.last_turn_at)}
                  </span>
                </div>
              </div>
              <div className="project-detail-actions">
                <button
                  type="button"
                  onClick={() => setSelectedId(null)}
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={() => onOpenChat(detail.conversation.id)}
                >
                  Open chat
                </button>
                <button
                  type="button"
                  onClick={() => void downloadZip()}
                  disabled={actionBusy !== null}
                >
                  {actionBusy === 'zip' ? 'Zipping…' : 'Download ZIP'}
                </button>
                <button
                  type="button"
                  onClick={() => void driveSync()}
                  disabled={actionBusy !== null}
                >
                  {actionBusy === 'drive' ? 'Syncing…' : 'Sync to Google Drive'}
                </button>
              </div>
              {actionMsg ? (
                <div className="project-action-msg muted small">
                  {actionMsg}
                </div>
              ) : null}
            </header>

            <nav className="project-tabs">
              {(
                [
                  'transcript',
                  'images',
                  'music',
                  'speech',
                  'captions',
                  'videos',
                  'documents',
                ] as TabId[]
              ).map((id) => (
                <button
                  key={id}
                  type="button"
                  className={
                    'project-tab' + (tab === id ? ' project-tab-active' : '')
                  }
                  onClick={() => setTab(id)}
                >
                  {id}
                </button>
              ))}
            </nav>

            <div className="project-tab-body">
              {tab === 'transcript' ? <TranscriptTab detail={detail} /> : null}
              {tab === 'images' ? <ImagesTab detail={detail} /> : null}
              {tab === 'music' ? <MusicTab detail={detail} /> : null}
              {tab === 'speech' ? <SpeechTab detail={detail} /> : null}
              {tab === 'captions' ? <CaptionsTab detail={detail} /> : null}
              {tab === 'videos' ? <VideosTab detail={detail} /> : null}
              {tab === 'documents' ? <DocumentsTab detail={detail} /> : null}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
